<template>
  <section id="footer" class="footer" style="position: relative">
    <footer class="footer-content">
      <img v-if="isMain" class="footer-image" src="~/assets/pages/footer/BgFooter.webp" alt="背景画像" />
      <!-- 会社情報 -->
      <div class="main-wrapper">
        <div class="left-box">
          <!-- ロゴ -->
          <img class="logo" src="~/assets/pages/common/Logo_Collar.svg" alt="天比登都" />
        </div>
        <div class="right-box">
          <!-- タイトル -->
          <div class="title">
            <div class="title-name">{{ $t('util.title') }}</div>
            <div class="sub-title-name">{{ $t('util.sub_title') }}</div>
          </div>
          <!-- 概要 -->
          <div class="description">
            <div class="text">{{ $t('util.title_and_sub_title_plus_text1') }}</div>
          </div>
          <!-- 会社情報 -->
          <div class="company-content">
            <!-- 郵便番号 -->
            <div class="list postcode">
              <div class="svg icon" />
              <div class="text">{{ $t('util.post_code') }}</div>
            </div>
            <!-- 住所 -->
            <div class="list address">
              <div class="svg icon" />
              <div class="text" :class="localeClass">{{ $t('util.company_address') }}</div>
            </div>
            <!-- 電話番号 -->
            <div class="list contact-number">
              <div class="svg icon" />
              <a v-if="isSP" :href="`tel:${$t('util.telephone_address')}`" class="text">
                {{ $t('util.telephone_address') }}
              </a>
              <div v-else class="text">
                {{ $t('util.telephone_address') }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- フッター部 -->
      <div class="footer-wrapper">
        <div class="site-map">
          <!-- 利用規約 -->
          <button class="terms-of-use small-text" @click="openTermsOfUseModal">
            {{ $t('util.terms_of_use') }}
          </button>
          <!-- 宿屋約款 -->
          <button class="inn-general-terms small-text" @click="openStayContract">
            {{ $t('util.inn_general_terms') }}
          </button>
          <!-- プライバシーポリシー -->
          <button
            class="privacy-policy small-text"
            @click="openPrivacyPolicyModal"
          >
            {{ $t('util.privacy_policy') }}
          </button>
          <!-- お問い合わせ -->
          <button class="contact small-text" @click="transitionContact">
            {{ $t('util.information') }}
          </button>
        </div>

        <!-- SNS -->
        <div class="sns-content">
          <button class="instagram icon-and-text" @click="openExternalUrl($t('sns.instagram.url'))">
            <span class="svg-icon" />
            <span class="text">Instagram</span>
          </button>
        </div>
        <div class="border" :class="localeClass" />
        <div class="copyright">
          {{ $t('util.copyright') }}
        </div>
      </div>
    </footer>
  </section>
</template>
<script setup lang="ts">
import { useMounted, useWindowSize } from "@vueuse/core";
import { actionTransition, transitionType, actionNewTabTransition, actionExternalNewTabTransition } from '~/models/Utility'
import { textModalCategory } from '@/models/TextModal'

const emit = defineEmits({
  'open-text-modal': (textModalCategory: textModalCategory) => true,
})

defineProps<{
  // メインページから呼び出されているか
  isMain: boolean
}>()

const openPrivacyPolicyModal = () => {
  emit('open-text-modal', textModalCategory.privacyPolicy)
}
const openStayContract = () => {
  actionNewTabTransition(transitionType.StayContract)
}
const openTermsOfUseModal = () => {
  emit('open-text-modal', textModalCategory.teamOfUse)
}
const { locale } = useI18n()
const route = useRoute()
const localeClass = computed(() => `-${locale.value}`)
const localePath = useLocalePath()
const transitionContact = () => {
  // 既にお問い合わせ画面にいる場合は上部にスクロールさせる
  const url = localePath(transitionType.ContactPage)
  if (route.path === url) {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
    return
  }
  actionTransition(url)
}
const openExternalUrl = (url: string) => {
  actionExternalNewTabTransition(url)
}

const isMounted = useMounted()
// SP表示かどうか
const { width } = useWindowSize()
const isSP = computed(() => {
  if (!isMounted.value) return true
  return width.value <= 767
});
</script>

<style lang="scss" scoped>
@use '@/assets/styles/mixins' as m;
@use '@/assets/styles/variables' as v;
@use '@/assets/styles/util' as u;

#footer {
  background: v.$bg-footer-top-color;
  width: 100%;

  .footer-content {
    width: 100%;
    padding: 0;
  }
}

/** アクセント画像 */
.footer-image {
  height: 368px;
  object-fit: cover;
  @include m.sp() {
    height: 72px;
  }
}

/** 会社情報 */
.main-wrapper {
  display: flex;
  justify-content: center;
  padding: 133px 0 128px;
  @include m.sp() {
    display: block;
    padding: 64px 0 0;
  }

  /** ロゴ */
  .left-box {
    padding: 10px 150px 10px 0;
    @include m.sp() {
      padding: 0 119px;
    }

    .logo {
      height: 226px;
      @include m.sp() {
        height: 149px;
      }
    }
  }

  /** 施設情報 */
  .right-box {
    .title {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
    }
    .title-name {
      font-size: 1.75rem;
      margin-right: 1rem;
      letter-spacing: 4.2px;
      line-height: 49px;
    }
    .sub-title-name {
      letter-spacing: 2.4px;
      line-height: 28px;
    }
    .description {
      margin-top: 20px;
      line-height: 28px;
      letter-spacing: 2.4px;
    }
    .svg {
      display: inline-block;
      height: 14px;
      width: 14px;
      background: v.$text-base-color;
      mask-size: contain;
      mask-repeat: no-repeat;
      mask-position: center;
    }
    .company-content {
      font-size: 0.875rem;
      line-height: 28px;
      letter-spacing: 2.1px;
      margin-top: 32px;
      @include m.sp() {
        font-size: inherit;
        line-height: 24.5px;
        letter-spacing: 2.1px;
      }
      .list {
        margin-bottom: 12px;
        @include m.sp() {
          margin-bottom: 8px;
        }
      }
    }
    /* 郵便番号 */
    .postcode {
      width: 100%;
      display: flex;
      align-items: center;
      > .icon {
        mask-image: url('~/assets/pages/common/IconPostmark.svg');
      }
      > .text {
        margin-left: 6px;
      }
    }
    /* 住所 */
    .address {
      width: 296px;
      width: 100%;
      display: flex;
      align-items: baseline;
      > .icon {
        mask-image: url('~/assets/pages/common/IconLocation.svg');
        @include m.sp() {
          width: 14px;
        }
      }
      > .text {
        margin-left: 6px;
        @include m.sp() {
          width: 90%;
        }
        &.-en {
          width: 54%;
          @include m.sp() {
            width: 90%;
          }
        }
      }
    }
    /* 電話番号 */
    .contact-number {
      width: 100%;
      display: flex;
      align-items: center;
      > .icon {
        mask-image: url('~/assets/pages/common/IconTelephone.svg');
      }
      > .text {
        margin-left: 6px;
      }
    }

    @include m.sp() {
      padding: 64px 47px 32px 16px;
    }
  }
}

/* フッター */
.footer-wrapper {
  background: v.$bg-footer-bottom-color;
  padding: 128px 0 48px;
  @include m.sp() {
    padding: 0 0 95px;
  }

  .site-map {
    display: flex;
    justify-content: center;
    .small-text {
      letter-spacing: 2.7px;
      line-height: 26px;
    }
    @include m.sp() {
      display: block;
      padding: 32px 100px 0 16px;
      .small-text {
        letter-spacing: 2.1px;
        line-height: 20px;
      }
    }
    button {
      text-align: left;
      color: v.$common-white-color;
      margin: 0 20px;
      @include m.sp() {
        margin: 0;
        display: block;
        &:not(:first-child) {
          margin-top: 16px;
        }
      }
    }
  }

  /* SNS */
  .sns-content {
    margin-top: 64px;
    text-align: center;
    @include m.sp() {
      margin-top: 64px;
    }

    .instagram {
      display: flex;
      align-items: center;
      margin: 0 auto;
      .svg-icon {
        display: inline-block;
        height: 16px;
        width: 16px;
        background: v.$common-white-color;
        mask-image: url('~/assets/pages/common/IconSnsInstagram.svg');
        mask-size: contain;
        mask-repeat: no-repeat;
        mask-position: center;
        @include m.sp() {
          height: 18px;
          width: 18px;
        }
      }
      .text {
        color: v.$common-white-color;
        margin-left: 16px;
        font-family: v.$accent-font-family;
        letter-spacing: 7.2px;
        @include m.sp() {
          letter-spacing: 6.4px;
          line-height: 23px;
        }
      }
    }
  }

  .border {
    background: v.$border-list-color;
    height: 1px;
    width: 54%;
    margin: 48px auto 0;
    @include m.sp() {
      width: 90%;
      margin: 32px auto;
    }
    &.-en {
      width: 50%;
      @include m.sp() {
        width: 90%;
      }
    }
  }

  .copyright {
    color: v.$common-white-color;
    font-family: v.$accent-font-family;
    font-weight: v.$font-weight-light;
    font-size: 0.875rem;
    margin-top: 50px;
    text-align: center;
    @include m.sp() {
      margin: 0;
      font-size: 0.75rem;
      letter-spacing: 4.8px;
    }
  }
}
</style>
